import * as React from "react";
import { type IColumn } from "@fluentui/react";
import { makeObservable, computed } from "mobx";
import { observer } from "mobx-react";
import { type UUID } from "../../utils/uuid";
import {
    type Department,
    type PreOpsAnswer,
    PreOpsAnswerChoice,
    type PreOpsChecklist,
    type PreOpsQuestion,
    type User,
    type Vehicle,
} from "../../api";
import { Severity, SeverityPill } from "./severity-pill";

export interface ListPreOpsAnswerItemCellProps {
    readonly answerId: UUID;
    readonly column: IColumn;
}

import { store } from "../../store";

@observer
export class ListPreOpsAnswerItemCell extends React.Component<ListPreOpsAnswerItemCellProps> {
    constructor(props: ListPreOpsAnswerItemCellProps) {
        super(props);
        makeObservable(this);
    }

    @computed private get preOpsAnswer(): PreOpsAnswer | undefined {
        return store.repositoryPreOpsAnswers.byId(this.props.answerId);
    }

    @computed private get preOpsQuestion(): PreOpsQuestion | undefined {
        if (this.preOpsAnswer) {
            return store.repositoryPreOpsQuestionVersions.byId(this.preOpsAnswer.questionVersionId);
        }
    }

    @computed private get preOpsChecklist(): PreOpsChecklist | undefined {
        if (this.preOpsQuestion) {
            return store.repositoryPreOpsChecklists.byId(this.preOpsQuestion.checklistId);
        }
    }

    @computed private get vehicle(): Vehicle | undefined {
        if (this.preOpsAnswer) {
            return store.repositoryVehicles.byId(this.preOpsAnswer.vehicleId);
        }
    }
    @computed private get department(): Department | undefined {
        if (this.vehicle && this.vehicle.departmentId) {
            return store.repositoryDepartments.byId(this.vehicle.departmentId);
        }
    }

    @computed private get user(): User | undefined {
        if (this.preOpsAnswer) {
            return store.repositoryUsers.byId(this.preOpsAnswer.userId);
        }
    }

    @computed private get severity(): Severity | undefined {
        if (!this.preOpsAnswer) {
            return;
        }

        switch (this.preOpsAnswer.choice) {
            case PreOpsAnswerChoice.GOOD:
                return Severity.LIGHT;
            case PreOpsAnswerChoice.YES:
                return Severity.LIGHT;
            case PreOpsAnswerChoice.OK:
                return Severity.MEDIUM;
            case PreOpsAnswerChoice.CRITICAL:
                return Severity.SEVERE;
            case PreOpsAnswerChoice.NO:
                return Severity.SEVERE;
        }
    }

    public render(): JSX.Element {
        const { column } = this.props;
        switch (column.key) {
            case "time":
                return (
                    <span>
                        {this.preOpsAnswer && store.i18n.formatDateTime(this.preOpsAnswer.time)}
                    </span>
                );
            case "vehicle":
                return <span>{this.vehicle?.serialNumber}</span>;
            case "department":
                return <span>{this.department?.label}</span>;
            case "user":
                return <span>{this.user && store.i18n.formatUserFullName(this.user)}</span>;
            case "checklistLabel":
                return <span>{this.preOpsChecklist?.label}</span>;
            case "question":
                return <span style={{ whiteSpace: "pre-wrap" }}>{this.preOpsQuestion?.body}</span>;
            case "choice":
                return this.severity && this.preOpsAnswer ? (
                    <SeverityPill
                        label={store.i18n.t(
                            `schema.preOpsAnswerChoice.${this.preOpsAnswer.choice}`,
                        )}
                        severity={this.severity}
                    />
                ) : (
                    <span />
                );
            case "key":
                return <span>{this.props.answerId}</span>;
            default:
                return <></>;
        }
    }
}
